import * as Sentry from "@sentry/browser";

export function initSentry() {
  Sentry.init({
    enabled: !import.meta.env?.DEV,

    dsn: "https://2ecc3d3af2275334f61f4758f95f82e7@o4505304938381312.ingest.us.sentry.io/4508855197433856",
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // tracesSampleRate: 0.025,
    // profilesSampleRate: 0.01,
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    tracePropagationTargets: ["localhost"],

    // Session Replay
    replaysSessionSampleRate: 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.05, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // Add Shopify-specific context
    beforeSend(event, hint) {
      const error = hint.originalException || hint.syntheticException;
      const errorMessage = (error as any | null)?.message || event.exception?.values?.[0]?.value || "";
      const stackTrace = event.exception?.values?.[0]?.stacktrace?.frames || [];
      const userAgent = event.request?.headers?.["User-Agent"] || navigator.userAgent || "";

      // Filter out non-actionable third-party errors

      // 1. Shopify infrastructure errors - BUT only if they don't involve your code
      if (stackTrace.some((frame) => frame.filename?.includes("storefront-banner"))) {
        return null; // Filter out Shopify privacy banner network issues
      }

      // Only filter Shopify media analytics if it's purely their script failing
      if (
        errorMessage.includes("load error:") &&
        errorMessage.includes("shopifycloud/media-analytics") &&
        !isFromYourCode(stackTrace)
      ) {
        return null; // Filter out Shopify analytics load failures not caused by your code
      }

      // 2. Android WebView issues - BUT only in social media apps
      if (
        errorMessage.includes("Java object is gone") &&
        (userAgent.includes("Facebook") || userAgent.includes("Instagram") || userAgent.includes("FBAN"))
      ) {
        return null; // Filter out WebView issues only in social apps
      }

      // 3. iOS WebKit messageHandlers - BUT only in social media apps or specific contexts
      if (
        errorMessage.includes("window.webkit.messageHandlers") &&
        errorMessage.includes("undefined is not an object") &&
        (userAgent.includes("Facebook") || userAgent.includes("Instagram") || !isFromYourCode(stackTrace))
      ) {
        return null; // Filter out only when not from your code
      }

      // 4. Browser View Transition API limitations - these are genuinely not actionable
      if (errorMessage.includes("Skipping view transition")) {
        return null; // Safe to filter - browser API limitation
      }

      // 5. Klaviyo storage errors - BUT only if they're purely from Klaviyo code
      if (
        stackTrace.some((frame) => frame.filename?.includes("klaviyo") || frame.filename?.includes("onsite/js")) &&
        !isFromYourCode(stackTrace)
      ) {
        return null; // Filter only when error is purely from Klaviyo
      }

      // 6. Generic browser storage security errors (private browsing, etc.)
      if (
        errorMessage.includes("Access is denied for this document") &&
        (errorMessage.includes("sessionStorage") || errorMessage.includes("localStorage")) &&
        !isFromYourCode(stackTrace)
      ) {
        return null; // Filter out private browsing storage issues
      }

      // 7. Network errors from external services - BUT preserve your fetch errors
      if (errorMessage.includes("Failed to fetch")) {
        // Only filter if it's clearly from third-party services AND not involving your code
        const isThirdPartyOnly =
          stackTrace.some(
            (frame) =>
              frame.filename?.includes("wistia.net") ||
              frame.filename?.includes("hcaptcha.com") ||
              frame.filename?.includes("klaviyo"),
          ) && !isFromYourCode(stackTrace);

        if (isThirdPartyOnly) {
          return null; // Filter only pure third-party fetch failures
        }
        // Otherwise, keep the error as it might be your fetch call failing
      }

      // 8. Chrome Lighthouse testing errors - safe to filter
      if (userAgent.includes("Chrome-Lighthouse") || errorMessage.includes("Chrome-Lighthouse")) {
        return null; // Filter out Lighthouse testing artifacts
      }

      // 9. Browser extension errors - safe to filter
      if (
        stackTrace.some(
          (frame) =>
            frame.filename?.includes("extension://") ||
            frame.filename?.includes("moz-extension://") ||
            frame.filename?.includes("safari-extension://"),
        )
      ) {
        return null; // Filter out browser extension errors
      }

      // 10. Be more careful with "Load failed" filtering
      if (errorMessage.includes("Load failed") && !isFromYourCode(stackTrace)) {
        return null; // Filter generic load failures from external sources
        // Keep load failures that might be from your theme's dynamic imports
      }

      event.tags = {
        ...event.tags,
        "shopify.theme": window.Shopify?.theme?.name || "unknown",
        "shopify.shop": window.Shopify?.shop || "unknown",
      };

      return event;
    },

    // Additional filtering for specific error types
    ignoreErrors: [
      // Ignore generic errors without context
      "<unknown>",
      "Non-Error promise rejection captured with value: Object Not Found",
      "Script error.",
      "Non-Error exception captured",

      // Ignore user cancellation actions
      "AbortError: Share canceled",
      "The user aborted a request",

      // Ignore browser API unavailable errors
      "Can't find variable: HTMLDialogElement",

      // Ignore ad blocker related errors
      "window.dataLayer is not defined",
    ],

    // URL patterns to ignore (third-party domains)
    denyUrls: [
      // External video/media services
      /fast\.wistia\.net/i,

      // Klaviyo - but only their onsite scripts, not potential API calls from your code
      /static\.klaviyo\.com.*\/onsite\//i,

      // Social media apps
      /fb\.com/i,
      /facebook\.com/i,
      /instagram\.com/i,

      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,

      // Ad networks and analytics
      /googleadservices\.com/i,
      /googlesyndication\.com/i,
      /doubleclick\.net/i,

      // Shopify Web Pixel Manager
      /\/wpm@.*\/web-pixel-.*\/sandbox\//i,
    ],
  });
}

// Helper function to check if error originates from your theme/app code
function isFromYourCode(frames: Sentry.StackFrame[]) {
  return frames.some((frame) => {
    const filename = frame.filename || "";
    return (
      filename.includes("/cdn/shop/t/") || // Shopify theme assets
      (filename.includes(window.location.hostname) &&
        !filename.includes("klaviyo") &&
        !filename.includes("shopifycloud"))
    );
  });
}

export { Sentry };
